/**
 * FIFO realized P/L for a single closing order.
 *
 * Computes lazily on demand (e.g. when the order detail drawer opens) so we
 * don't pay the cost up-front for orders the user never inspects. Pure
 * function, zero IO — caller hands in everything we need.
 *
 * Inputs
 * ------
 *  - target          The order whose realized gain/loss we want.
 *  - accountOrders   Every order on the user's accounts (will be filtered
 *                    internally to same accountId + same symbol). Pass the
 *                    UNFILTERED set — a "last 30 days" UI filter would hide
 *                    older buys whose cost basis we still need.
 *  - splits          Optional split events for the target symbol, from
 *                    `useRealHistoricalPrices`. Each event scales every
 *                    open lot's qty and cost-per-share to keep
 *                    qty × costPerShare invariant across the split.
 *
 * Algorithm
 * ---------
 *  1. Filter relevant orders: same accountId, same asset, status in
 *     {filled, partial}, filledQty > 0. Sort ascending by submittedAt.
 *  2. Walk forward keeping a FIFO queue of open lots `{ qty, costPerShare }`.
 *     Lot qty carries its sign — positive = long, negative = short.
 *     Same-sign new order → push (opens). Opposite-sign → consume FIFO
 *     (closes).
 *  3. Splits applied between fills: scan the splits list for any event
 *     dated on/before the next order's date that hasn't yet been applied;
 *     scale every lot's qty by `ratio` and costPerShare by `1/ratio`.
 *     Total_value = qty × costPerShare is preserved.
 *  4. When we reach the target order, accumulate realized $ and cost basis
 *     for its matched portion only. Return immediately.
 *  5. Realized fraction = realized_usd / cost_basis_of_matched_lots.
 *
 * Returns
 * -------
 *  - { realizedUsd, realizedFraction, matchedQty } when the target order
 *    closed any prior exposure (full or partial).
 *  - null when the target is purely opening, has zero filledQty, or is in
 *    a non-fill status.
 *
 * Limitations
 * -----------
 *  - Fees / commissions ignored (Alpaca is commission-free).
 *  - Stock dividends paid in shares not modelled (rare for retail).
 *  - "Long + short on same ticker simultaneously" not common for retail —
 *    we handle the case cleanly via signed qty but don't assert against it.
 */

import type { Order } from "@/data/mock-orders";
import type { TickerSplit } from "@/lib/equity-reconstruction";

export interface RealizedProfit {
  /** Realized profit/loss in USD for the matched portion of the target order. */
  realizedUsd: number;
  /** Realized profit/loss as a fraction of the cost basis of the matched
   *  lots. 0.05 → +5%, -0.10 → -10%. */
  realizedFraction: number;
  /** Number of shares from the target order that matched against existing
   *  open lots. Always ≤ filledQty. When less, the remainder opens new
   *  exposure (the "open" portion is reported separately by callers if
   *  they want to surface it; this function focuses on the closed portion). */
  matchedQty: number;
}

/**
 * Order counts within the current "round trip" — the run of fills
 * since the last time the position was flat (or since the very first
 * fill ever if the user has never gone flat). Counters reset every
 * time `(account, asset)` returns to zero shares; the order that
 * flattens the position is the LAST order of that round trip and its
 * counts include itself.
 *
 * Industry convention: a "round trip" is one full buy-sell cycle of
 * the same instrument (or short-cover for shorts). Showing the
 * constituent buy / sell counts on the order detail makes it clear
 * how many fills built up the trade so far.
 */
export interface RoundTripCounts {
  /** Buy fills in the current round trip, inclusive of the target. */
  buys: number;
  /** Sell fills in the current round trip, inclusive of the target. */
  sells: number;
}

/**
 * Combined FIFO result for an order. Always includes the post-trade
 * position (signed qty held after the order is processed) and the
 * round-trip order counts; realized P/L is null when the order purely
 * opens.
 */
export interface OrderFifoResult {
  /** Signed qty held in (account, asset) after this order processes.
   *  Positive = net long, negative = net short, 0 = flat. */
  positionAfter: number;
  /** Realized P/L details for this order's closed portion, or null when
   *  the order purely opens (matched nothing). */
  realized: RealizedProfit | null;
  /** Buy / sell fill counts within the current round trip — see
   *  {@link RoundTripCounts}. */
  roundTrip: RoundTripCounts;
}

/**
 * Compute realized P/L AND post-trade position for `target` against the
 * FIFO queue formed by earlier fills on the same `(accountId, asset)`.
 * See file-level docblock for the full contract.
 *
 * Returns null when the target order is non-fillable (canceled, expired,
 * replaced, open) or has zero filledQty — there's no fill to anchor a
 * "position after" on. For successful fills (filled / partial), always
 * returns `{ positionAfter, realized }` with `realized` set to null when
 * the order purely opens.
 */
export function calcOrderFifoResult(
  target: Order,
  accountOrders: readonly Order[],
  splits: readonly TickerSplit[] = []
): OrderFifoResult | null {
  if (target.status !== "filled" && target.status !== "partial") return null;
  if (!Number.isFinite(target.filledQty) || target.filledQty <= 0) return null;

  const relevant = accountOrders
    .filter(
      (o) =>
        o.accountId === target.accountId &&
        o.asset === target.asset &&
        (o.status === "filled" || o.status === "partial") &&
        Number.isFinite(o.filledQty) &&
        o.filledQty > 0
    )
    .sort(sortByTime);

  // Sort splits ascending. Parsed dates are "YYYY-MM-DD" so a string
  // compare is correct chronological order.
  const sortedSplits = [...splits].sort((a, b) => a.date.localeCompare(b.date));

  // Open lots queue. qty carries direction sign: + for long, - for short.
  // costPerShare always stored as a positive USD figure.
  type Lot = { qty: number; costPerShare: number };
  const lots: Lot[] = [];

  // Pointer into sortedSplits — events strictly before/equal to the next
  // order's date get applied at the start of each loop iteration. The
  // pointer-only walk keeps splits work O(n + s) instead of O(n*s).
  let splitIdx = 0;

  function applySplitsUpTo(orderIso: string): void {
    const orderDate = orderIso.slice(0, 10);
    while (
      splitIdx < sortedSplits.length &&
      sortedSplits[splitIdx]!.date <= orderDate
    ) {
      const ratio = sortedSplits[splitIdx]!.ratio;
      // Defensive: a non-finite or non-positive ratio is bad data; skip
      // rather than mangle the queue.
      if (Number.isFinite(ratio) && ratio > 0) {
        for (const lot of lots) {
          lot.qty *= ratio;
          lot.costPerShare /= ratio;
        }
      }
      splitIdx++;
    }
  }

  let realizedUsd = 0;
  let costBasis = 0;
  let matchedQty = 0;
  let reachedTarget = false;

  // Round-trip fill counters — reset whenever the position is flat at
  // the START of a new order (i.e. the previous order closed a round
  // trip, or this is the very first fill ever). The counter increments
  // INCLUDE the current order so the closing fill's display reads
  // "this round trip ended with N buys / M sells".
  let rtBuys = 0;
  let rtSells = 0;
  let rtBuysAtTarget = 0;
  let rtSellsAtTarget = 0;

  for (const o of relevant) {
    applySplitsUpTo(o.submittedAt);

    // Cycle-start detection: position is exactly flat going into this
    // order. Reset round-trip counters before counting this fill.
    // Using sub-ε threshold to absorb floating-point residue from
    // chained matches.
    const positionBefore = lots.reduce((s, l) => s + l.qty, 0);
    if (Math.abs(positionBefore) < 1e-9) {
      rtBuys = 0;
      rtSells = 0;
    }

    if (o.side === "buy") rtBuys += 1;
    else rtSells += 1;

    const orderSign = o.side === "buy" ? 1 : -1;
    let remaining = o.filledQty;
    const isTarget = o.id === target.id;

    while (remaining > 0) {
      const head = lots[0];
      const headSign = head ? (head.qty > 0 ? 1 : -1) : 0;

      if (!head || headSign === orderSign) {
        // Same direction (or empty queue) → this fill opens a new lot.
        // Push as one chunk; FIFO order across fills is preserved by the
        // outer loop's chronological sort.
        lots.push({
          qty: orderSign * remaining,
          costPerShare: o.avgFillPrice
        });
        remaining = 0;
        break;
      }

      // Opposite direction → close FIFO.
      const headAbs = Math.abs(head.qty);
      const matchAbs = Math.min(headAbs, remaining);

      // Per-share P/L:
      //   long lot closed by sell  → sell.price − lot.cost   (positive when up)
      //   short lot closed by buy  → lot.cost  − buy.price   (positive when down)
      const perShare =
        headSign === 1
          ? o.avgFillPrice - head.costPerShare
          : head.costPerShare - o.avgFillPrice;
      const matchedProfit = perShare * matchAbs;
      const matchedCost = head.costPerShare * matchAbs;

      if (isTarget) {
        realizedUsd += matchedProfit;
        costBasis += matchedCost;
        matchedQty += matchAbs;
      }

      // Reduce or pop the head lot.
      if (matchAbs >= headAbs - 1e-9) {
        lots.shift();
      } else {
        head.qty = head.qty + (headSign === 1 ? -matchAbs : matchAbs);
      }
      remaining -= matchAbs;
    }

    if (isTarget) {
      // Snapshot round-trip counters at the moment we hit the target.
      // Doing this AFTER the lot updates above means the count
      // reflects the state including the target fill itself, which is
      // what the UI needs to render "this fill closed the trip with
      // N buys / M sells".
      rtBuysAtTarget = rtBuys;
      rtSellsAtTarget = rtSells;
      reachedTarget = true;
      break;
    }
  }

  // Should always be true given the filter: target itself satisfies the
  // (account, asset, fillable, qty>0) predicate and so appears in
  // `relevant`. The defensive check guards against future changes to
  // upstream data shapes.
  if (!reachedTarget) return null;

  // Position after the target order = sum of remaining lot quantities.
  // Sign carries direction: positive = net long, negative = net short,
  // 0 = flat (round small floating-point residue from chained matches).
  const positionAfterRaw = lots.reduce((sum, l) => sum + l.qty, 0);
  const positionAfter =
    Math.abs(positionAfterRaw) < 1e-9 ? 0 : positionAfterRaw;

  const realized: RealizedProfit | null =
    matchedQty > 0
      ? {
          realizedUsd,
          realizedFraction: costBasis > 0 ? realizedUsd / costBasis : 0,
          matchedQty
        }
      : null;

  return {
    positionAfter,
    realized,
    roundTrip: { buys: rtBuysAtTarget, sells: rtSellsAtTarget }
  };
}

/**
 * Backward-compatible thin wrapper kept for callers that only want the
 * realized portion. Returns null both when the order is non-fillable
 * AND when it purely opens (matched nothing).
 */
export function calcRealizedProfitFifo(
  target: Order,
  accountOrders: readonly Order[],
  splits: readonly TickerSplit[] = []
): RealizedProfit | null {
  const r = calcOrderFifoResult(target, accountOrders, splits);
  return r?.realized ?? null;
}

function sortByTime(a: Order, b: Order): number {
  // Primary: submittedAt ascending. Tie-break by filledAt then id for
  // deterministic ordering across fills that share a clock minute.
  if (a.submittedAt !== b.submittedAt) {
    return a.submittedAt < b.submittedAt ? -1 : 1;
  }
  const af = a.filledAt ?? "";
  const bf = b.filledAt ?? "";
  if (af !== bf) return af < bf ? -1 : 1;
  return a.id.localeCompare(b.id);
}
